import { Spacing } from '@persona-id/design-tokens';
import { useMemo, VFC } from 'react';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import {
  MatchIconAlert,
  MatchIconPositive,
  MatchIconWarning,
} from 'dashboard/components/ReportResult/MatchIcons';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import TableLayout, { TableIconTD, TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import {
  ReportMiddeskBusinessLookup,
  Task,
  Tin,
} from 'dashboard/models/report/integration/middesk-business-lookup';
import Card from 'design-system/components/Card';
import Sticker, { LabelBackgrounds, StickerProps } from 'design-system/components/Sticker';
import { FontWeight } from 'lib/interfaces/ThemeSerialized';

export enum MiddeskStatus {
  Open = 'open',
  Pending = 'pending',
  InAudit = 'in_audit',
  InReview = 'in_review',
  Approved = 'approved',
  Rejected = 'rejected',
}

const StyledTableTH = styled(TableTH)`
  width: ${(props) => props.width ?? 'auto'};
  padding-top: 4px;
  padding-bottom: 4px;
  font-weight: ${FontWeight.Medium};
`;

const PaddedSticker = styled(Sticker)`
  margin-left: ${Spacing[4]};
`;

const redactedSticker = <Sticker>Redacted</Sticker>;

const taskStatusIcon = (taskStatus?: string) => {
  if (!taskStatus) {
    return <></>;
  }

  switch (taskStatus.toLowerCase()) {
    case 'failure':
      return <MatchIconAlert />;
    case 'warning':
      return <MatchIconWarning />;
    case 'success':
      return <MatchIconPositive />;
  }
};

const taskStatusSticker = (task: Task) => {
  let variant: StickerProps['variant'] = 'basic';

  switch (task.status?.toLowerCase()) {
    case 'failure':
      variant = 'danger';
      break;
    case 'warning':
      variant = 'caution';
      break;
    case 'success':
      variant = 'success';
      break;
  }

  return <Sticker variant={variant}>{task['sub-label']}</Sticker>;
};

const getTinResult = (tin?: Tin) => {
  if (tin?.verified) {
    return <Sticker variant='success'>Verified</Sticker>;
  } else if (tin?.mismatch) {
    return <Sticker variant='danger'>Mismatch</Sticker>;
  } else if (tin?.unknown) {
    return <Sticker variant='caution'>Unknown</Sticker>;
  } else {
    return <Sticker variant='danger'>{tin?.error}</Sticker>;
  }
};

const combineAddressLines = (line1?: string, line2?: string) =>
  line2 ? `${line1}, ${line2}` : line1;

interface MiddeskBusinessLookupProps {
  report: ReportMiddeskBusinessLookup;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const MiddeskBusinessLookup: VFC<MiddeskBusinessLookupProps> = ({
  report,
  isRedacted,
  multiCardFormat = false,
}: MiddeskBusinessLookupProps) => {
  const tasks = report.attributes.result?.review?.tasks;
  const middeskStatus = report.attributes.result?.status;
  const [variant, label]: [LabelBackgrounds, string] = useMemo(() => {
    if (middeskStatus === MiddeskStatus.Open) {
      return ['yellow', 'Open'];
    } else if (middeskStatus === MiddeskStatus.Pending) {
      return ['yellow', 'Pending'];
    } else if (middeskStatus === MiddeskStatus.InAudit) {
      return ['yellow', 'In Audit'];
    } else if (middeskStatus === MiddeskStatus.InReview) {
      return ['grey', 'In Review'];
    } else if (middeskStatus === MiddeskStatus.Approved) {
      return ['green', 'Approved'];
    } else if (middeskStatus === MiddeskStatus.Rejected) {
      return ['red', 'No match'];
    } else {
      return ['grey', 'N/A'];
    }
  }, [middeskStatus]);

  const reviewTasks = (
    <>
      <Card.Title>
        Middesk Business Lookup Report
        {middeskStatus != null && !isRedacted && (
          <PaddedSticker data-test='review-task-table_status-sticker' variant={variant}>
            {label}
          </PaddedSticker>
        )}
      </Card.Title>
      <TableLayout
        dataTest='review-task-table'
        isLoading={false}
        showHeader={true}
        nullState={null}
        headerTR={
          <>
            <StyledTableTH>{/* Empty header for icon column */}</StyledTableTH>
            <StyledTableTH>Category</StyledTableTH>
            <StyledTableTH>Result</StyledTableTH>
            <StyledTableTH>Message</StyledTableTH>
          </>
        }
      >
        {tasks?.map((task, idx) => (
          <TableTR id={`review-task-table__row--${idx}`} key={`review-task-table__row--${idx}`}>
            <TableIconTD>{taskStatusIcon(task.status)}</TableIconTD>
            <TableTD>{isRedacted ? redactedSticker : task.label}</TableTD>
            <TableTD>{isRedacted ? redactedSticker : taskStatusSticker(task)}</TableTD>
            <TableTD>{isRedacted ? redactedSticker : task.message}</TableTD>
          </TableTR>
        ))}
      </TableLayout>
    </>
  );

  const tinContent = (
    <>
      <Card.Title>Tax Identification Number - Middesk</Card.Title>
      <AttributeGrid colCount={3}>
        <AttributeItem
          title='TIN'
          value={isRedacted ? redactedSticker : report.attributes.result?.tin?.tin}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='tin-content__tin-number'
        />
        <AttributeItem
          title='Business Name'
          value={isRedacted ? redactedSticker : report.attributes.result?.tin?.name}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='tin-content__tin-name'
        />
        <AttributeItem
          title='Result'
          value={isRedacted ? redactedSticker : getTinResult(report.attributes.result?.tin)}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='tin-content__tin-name'
        />
      </AttributeGrid>
    </>
  );

  const websiteContent = (
    <>
      <Card.Title>Website - Middesk</Card.Title>
      <AttributeGrid colCount={3}>
        <AttributeItem
          title='URL'
          value={
            isRedacted ? (
              redactedSticker
            ) : (
              <a href={report.attributes.result?.website?.url}>
                {report.attributes.result?.website?.url}
              </a>
            )
          }
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='website-content__url'
        />
        <AttributeItem
          title='Title'
          value={isRedacted ? redactedSticker : report.attributes.result?.website?.title}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='website-content__ur'
        />
        <AttributeItem
          title='Description'
          value={isRedacted ? redactedSticker : report.attributes.result?.website?.description}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='website-content__ur'
        />
      </AttributeGrid>
    </>
  );

  const addressesContent = (
    <>
      <Card.Title>Addresses - Middesk</Card.Title>
      <TableLayout
        dataTest='addresses-content-table'
        isLoading={false}
        showHeader={true}
        isEmpty={report.attributes.result?.addresses?.length === 0}
        nullState={null}
        headerTR={
          <>
            <StyledTableTH>Street addresses</StyledTableTH>
            <StyledTableTH>City, State</StyledTableTH>
            <StyledTableTH>Postal code</StyledTableTH>
          </>
        }
      >
        {report.attributes.result?.addresses?.map((address, idx) => (
          <TableTR
            id={`addresses-content-table__row--${idx}`}
            key={`addresses-content-table--${idx}`}
          >
            <TableTD>
              {isRedacted
                ? redactedSticker
                : combineAddressLines(address['address-line1'], address['address-line2'])}
            </TableTD>
            <TableTD>{isRedacted ? redactedSticker : `${address.city}, ${address.state}`}</TableTD>
            <TableTD>{isRedacted ? redactedSticker : address['postal-code']}</TableTD>
          </TableTR>
        ))}
      </TableLayout>
    </>
  );

  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      {reviewTasks}
      {tinContent}
      {websiteContent}
      {addressesContent}
    </ReportResultLayout>
  );
};

export default MiddeskBusinessLookup;
